"use client";

import { LineItem, TaxCategory } from "@commercetools/platform-sdk";
import {
  AttributeOption,
  calcProductConfigValueChange,
  DeliveryOption,
  generatePartialConfig,
  getAttributesHash,
  getAttributeValueByIdOrIds,
  getDeliveryOption,
  ProductAttribute,
  ProductConfiguration,
  ProductConfigurationValue,
  ProductContextProvider,
  ProductContextValueWithPrices,
  ProductWarning,
  useProduct,
} from "@wttb/product-configurator";
import { parseAsJson, useQueryState } from "nuqs";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCart } from "@/features/basket/hooks/use-cart";
import { useDefaultPriceWithLoyalty } from "@/features/product/hooks/use-price";

type StoreProductState = {
  sku: string;
  deliveryOption: DeliveryOption | null;
};

const initialState: StoreProductState = {
  sku: "",
  deliveryOption: null,
};

type StoreProductContextValue = StoreProductState &
  ProductContextValueWithPrices & {
    attributesHash: Record<string, Record<string, AttributeOption>>;
    selectedOptions: ProductConfiguration;
    updateLineItemId?: string;
    updateOrderId?: string;
    attributeDisplayNameHash: Record<string, string>;
    taxCategory: TaxCategory | null;
    attributes: ProductAttribute[];
    warnings: ProductWarning[];
    thicknessesMap: Record<string, number> | null;
    artworkCheckPrice?: number;

    // methods
    setSelectedOptions: (selectedOptions: ProductConfiguration) => void;
    setUpdateLineItemId: (lineItemId: string) => void;
    setSkipPriceCalculation: (skipPriceCalculation: boolean) => void;
    setSelectedOption: (key: string, valueId: string | string[]) => void;
    setProductConfig: (updatedConfig: ProductConfiguration) => void;
    setDeliveryOption: (deliveryOption: DeliveryOption | null) => void;
    setArtworkCheckPrice: (artworkCheckPrice?: number) => void;
  };

export const StoreProductContext = createContext<StoreProductState>(initialState);
StoreProductContext.displayName = "StoreProductContext";

type ProductProviderProps = PropsWithChildren & {
  sku: string;
  taxCategory: TaxCategory | null;
  thicknessesMap: Record<string, number> | null;
};

type WrapperProps = PropsWithChildren & {
  attributes: ProductAttribute[];
  warnings: ProductWarning[];
  sku: string;
  taxCategory: TaxCategory | null;
  thicknessesMap: Record<string, number> | null;
};

export const ProductProvider = ({
  attributes,
  warnings,
  taxCategory,
  sku,
  thicknessesMap,
  children,
}: WrapperProps) => {
  return (
    <ProductContextProvider key={sku} attributes={attributes} warnings={warnings}>
      <ProductProviderWrapper
        key={sku}
        sku={sku}
        taxCategory={taxCategory}
        thicknessesMap={thicknessesMap}
      >
        {children}
      </ProductProviderWrapper>
    </ProductContextProvider>
  );
};

const ProductProviderWrapper = ({
  children,
  sku,
  taxCategory,
  thicknessesMap,
}: ProductProviderProps) => {
  const [storeProductState, setStoreProductState] = useState<StoreProductState>({
    ...initialState,
  });
  const [skipPriceCalculation, setSkipPriceCalculation] = useState<boolean>(false);

  const productContext = useProduct();

  const [selectedOptions, setSelectedOptionsQuery] = useQueryState<ProductConfiguration>(
    "selectedOptions",
    parseAsJson<ProductConfiguration>().withDefault({}),
  );
  const [updateLineItemId, setUpdateLineItemId] = useQueryState("updateKey");
  const [updateOrderId, setUpdateOrderId] = useQueryState("orderId");

  const [artworkCheckPrice, setArtworkCheckPrice] = useState<number>();

  const { data: cart } = useCart();

  const lineItem = useMemo(
    () => cart?.lineItems.find((lineItem: LineItem) => lineItem.id === updateLineItemId),
    [cart?.lineItems, updateLineItemId],
  );

  const artworkCheck = useMemo(
    () =>
      cart?.customLineItems.find(
        (customLineItem) => customLineItem.custom?.fields.lineItemKey === (lineItem?.key ?? ""),
      ),
    [cart?.customLineItems, lineItem],
  );

  useEffect(() => {
    const artworkCheckPrice = artworkCheck?.money.centAmount;
    if (artworkCheckPrice) {
      setArtworkCheckPrice(artworkCheckPrice);
    }
  }, [artworkCheck]);

  const lineItemConfig = lineItem?.custom?.fields.productConfig;

  const attributesHash = useMemo(() => {
    return getAttributesHash(productContext.attributes);
  }, [productContext.attributes]);

  const productConfig: ProductConfiguration = useMemo(
    () => ({
      ...productContext.config,
      ...(lineItemConfig ? JSON.parse(lineItemConfig) : {}),
      ...(Object.keys(selectedOptions).length
        ? generatePartialConfig(selectedOptions, attributesHash)
        : {}),
    }),
    [lineItemConfig, selectedOptions, attributesHash],
  );

  const {
    data: pricingData,
    isLoading,
    error,
    isError,
    fetchStatus,
    isPreviousData,
  } = useDefaultPriceWithLoyalty({
    sku,
    config: productConfig,
    includeQuantityCalculation: true,
    enabled: productContext.isConfigValid && !skipPriceCalculation,
  });

  useEffect(() => {
    if (JSON.stringify(productConfig) !== JSON.stringify(productContext.config)) {
      productContext.setProductConfig(productConfig);
    }
  }, [productConfig, productContext.config]);

  useEffect(() => {
    if (pricingData) {
      setStoreProductState((prev) => ({
        ...prev,
        deliveryOption: getDeliveryOption(pricingData, prev.deliveryOption),
      }));
    }
  }, [pricingData]);

  const attributeDisplayNameHash = useMemo(() => {
    return productContext.attributes.reduce(
      (acc, attribute) => {
        acc[attribute.key] = attribute.display;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [productContext.attributes]);

  const setSelectedOption = useCallback(
    async (key: string, value: ProductConfigurationValue) => {
      await setSelectedOptionsQuery((prev) => ({ ...prev, [key]: value }), { shallow: true });
    },
    [setSelectedOptionsQuery],
  );

  const setSelectedOptions = useCallback(
    async (selectedOptions: ProductConfiguration) => {
      await setSelectedOptionsQuery(selectedOptions, { shallow: true });
    },
    [setSelectedOptionsQuery],
  );

  const setConfigValue = useCallback(
    async (key: string, valueId: string | string[]) => {
      const value = getAttributeValueByIdOrIds(key, valueId, attributesHash);
      let newConfigValues: ProductConfiguration;

      if (Array.isArray(valueId)) {
        newConfigValues = valueId.reduce((acc, cur) => {
          return {
            ...acc,
            ...calcProductConfigValueChange({
              incompatibilitiesHash: productContext.incompatibilitiesHash,
              productConfig,
              attributesHash,
              valueId: cur,
              key,
            }),
          };
        }, {});
      } else {
        newConfigValues = calcProductConfigValueChange({
          incompatibilitiesHash: productContext.incompatibilitiesHash,
          productConfig,
          attributesHash,
          valueId,
          key,
        });
      }

      await setSelectedOptionsQuery((prev) => ({
        ...prev,
        ...newConfigValues,
        [key]: value,
      }));
      productContext.setProductConfig({ ...productConfig, ...newConfigValues, [key]: valueId });
    },
    [productConfig, attributesHash, productContext, setSelectedOption],
  );

  const setCustomConfigValue = useCallback(
    async (key: string, valueId: string | string[]) => {
      await setSelectedOption(key, valueId);
      productContext.setCustomConfigValue(key, valueId);
    },
    [productContext.setCustomConfigValue, setSelectedOption],
  );

  const setDeliveryOption = useCallback(
    (option: DeliveryOption) =>
      setStoreProductState((prev) => ({ ...prev, deliveryOption: option })),
    [],
  );

  const contextValue = useMemo(
    () =>
      ({
        ...productContext,
        ...storeProductState,
        updateOrderId,
        updateLineItemId,
        pricingResult: pricingData,
        isLoading: isPreviousData || (isLoading && fetchStatus !== "idle"),
        error,
        isError,
        sku,
        selectedOptions,
        attributeDisplayNameHash,
        attributesHash,
        taxCategory,
        thicknessesMap,
        artworkCheckPrice,
        //
        setConfigValue,
        setCustomConfigValue,
        setSelectedOptions,
        setSelectedOption,
        setUpdateLineItemId,
        setUpdateOrderId,
        setDeliveryOption,
        setSkipPriceCalculation,
        setArtworkCheckPrice,
      }) as unknown as StoreProductContextValue,
    [
      productContext,
      storeProductState,
      updateOrderId,
      updateLineItemId,
      pricingData,
      isPreviousData,
      isLoading,
      fetchStatus,
      error,
      isError,
      sku,
      selectedOptions,
      attributeDisplayNameHash,
      attributesHash,
      taxCategory,
      thicknessesMap,
      artworkCheckPrice,
      setConfigValue,
      setCustomConfigValue,
      setSelectedOptions,
      setSelectedOption,
      setUpdateLineItemId,
      setUpdateOrderId,
      setDeliveryOption,
      setArtworkCheckPrice,
    ],
  );

  return (
    <StoreProductContext.Provider value={contextValue}>{children}</StoreProductContext.Provider>
  );
};

export const useStoreProduct = () => {
  return useContext(StoreProductContext) as unknown as StoreProductContextValue;
};
